import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { concat } from 'lodash'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import LoadMore from '@/components/base/load-more'
import Section from '@/components/base/section'
import { Card } from '@/components/sections/grid'
import Spacer from '@/components/sections/spacer'
import { normalizeCollectionData } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { useGridMotion } from './motion'
import { WPHeaders, WPTaxonomy } from '@/types'

type GridServicesProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    post_type: string
    component_type: string
    collection: any[]
    filter: string
    headers: WPHeaders
    per_page: string
    filter_terms: WPTaxonomy[]
    use_filter: boolean
    filter_type: 'static' | 'dynamic'
    static_filter: WPTaxonomy
    static_filter_parent: 'ancestor' | 'children'
    use_pagination: boolean
    show_link: boolean
    className: string
  }>

const GridServices = ({
  post_type,
  collection: posts,
  className,
  headers: postHeaders,
  use_pagination = true,
  per_page,
  ...props
}: GridServicesProps) => {
  const [headers, setHeaders] = useState<WPHeaders>(postHeaders!)
  const [collection, setCollection] = useState<any[]>(normalizeCollectionData(posts || []))
  const [needsPagination, setNeedsPagination] = useState(headers['X-WP-TotalPages'] > 1)
  const [pagination, setPagination] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const { motionRef } = useGridMotion()

  const getParams = useCallback(
    (page: number) => {
      const perPage = use_pagination ? per_page : 1000
      const params = {
        per_page: perPage,
        page
      } as any

      return {
        params
      }
    },
    [use_pagination, per_page]
  )

  const getPosts = useCallback(
    async (page: number) => {
      const postType = post_type == 'post' ? 'posts' : post_type

      setFetching(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: `${API.BASE}/${postType}`,
        params: getParams(page)
      })

      setHeaders({
        'X-WP-Total': parseInt(headers['x-wp-total']),
        'X-WP-TotalPages': parseInt(headers['x-wp-totalpages'])
      })

      setCollection((previousCollection) => {
        const collection = normalizeCollectionData(data)
        return concat(previousCollection, collection)
      })

      setNeedsPagination(page < parseInt(headers['x-wp-totalpages']))

      setFetching(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [post_type, getParams]
  )

  useEffect(() => {
    if (pagination < 0) return
    getPosts(pagination)
  }, [pagination, getPosts])

  return (
    <>
      <Section ref={motionRef} {...props}>
        <Container>
          <Grid className="items-center gap-y-spacing-lg">
            <div className="col-span-2 md:col-span-6 lg:col-span-12">
              <div className="grid grid-cols-2 gap-spacing-xs md:grid-cols-2 lg:grid-cols-3">
                {collection?.map((item, index) => {
                  const cardProps = { ...item, image: item.image }
                  return (
                    <div key={`product-card-${index}`} className="col-span-2 md:col-span-1">
                      <Card {...cardProps} data-motion-card />
                    </div>
                  )
                })}
              </div>
            </div>
          </Grid>
        </Container>
      </Section>

      {use_pagination && needsPagination ? (
        <>
          <Spacer variant="md" />
          <Container>
            <LoadMore
              className={className}
              fetching={fetching}
              onClick={() => {
                setPagination((pagination) => {
                  return pagination < 0 ? 2 : pagination + 1
                })
              }}
            />
          </Container>
        </>
      ) : null}
    </>
  )
}

export default GridServices
